import { useCallback, useId, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useSearchParams } from 'react-router'

import { Skeleton } from '@/shared/layout/Skeleton'
import { SlotContent, SlotCount, SlotPanels, useLinkAccessStatus, useSlot, type RegisteredTab, type SlotContext } from '@/shared/registry'
import { NarrowPanelContext } from '@/shared/search/narrow-panel'
import { PageContainer, Tabs, tabPanelProps } from '@/shared/ui'
import { Puzzle } from '@/shared/ui/icons'

import type { Audit } from './audit'
import { CreatedModified } from './CreatedModified'
import { DetailsHeader, type DetailsHeaderProps } from './DetailsHeader'
import { DetailsSidebar, type DetailsMenuGroup, type DetailsMenuItem } from './DetailsSidebar'
import { withinPage } from './page-visit'

/** The query parameter naming the details page's open tab; absent for the first. */
const tabParam = 'tab'

/** One of a details page's own tabs. */
export interface DetailsTab {
  readonly id: string
  readonly label: string
  readonly content: ReactNode
}

/**
 * The slots a details page offers other features, named from its base, such as
 * `site-management.user-details`: tabs, side-menu links, header actions and panels (GW-19).
 */
export function detailsSlots(base: string) {
  return {
    tabs: `${base}.tabs`,
    menu: `${base}.menu`,
    actions: `${base}.actions`,
    panels: `${base}.panels`,
  } as const
}

export interface DetailsPageProps {
  readonly header: Omit<DetailsHeaderProps, 'actionSlot' | 'context' | 'refreshKey'>
  /** The base of the page's slot names (`detailsSlots`). */
  readonly slot: string
  /** The record's public id, handed to every contribution. */
  readonly recordId: string
  /** Whether the record is never changed again, handed to every contribution so it offers no add or edit. */
  readonly isReadOnly?: boolean
  /** The side menu's groups; contributed links follow in a group of their own. */
  readonly menu?: readonly DetailsMenuGroup[]
  /** The page's own tabs; contributed tabs follow. The first is shown unless the address's `tab` names another. */
  readonly tabs: readonly DetailsTab[]
  /** Contributed tabs' ids in the order the mockup shows them; the rest follow in registry order. */
  readonly tabOrder?: readonly string[]
  /** Shown at the end as "Created … by …" and "Modified … by …". */
  readonly audit?: Audit
  /** Shown above the tabs, such as a course's card. */
  readonly summary?: ReactNode
  /**
   * Changes when the record was changed on the page, so every tab, panel, count and registered
   * action reads it afresh. The side menu stays, keeping focus on the action that made the change.
   */
  readonly refreshKey?: number
}

/**
 * A details page, laid out as the mockups' `details/UserDetails.jsx`: the header, the side menu
 * of related pages, tabs of facts, panels and the "Created by" lines. What other features
 * register in its slots appears in them; the page imports nothing from them.
 */
export function DetailsPage(props: DetailsPageProps) {
  const intl = useIntl()
  const idPrefix = useId()
  const { recordId, tabs, tabOrder, refreshKey, isReadOnly = false } = props
  const slots = detailsSlots(props.slot)
  const context = useMemo<SlotContext>(() => (isReadOnly ? { recordId, isReadOnly } : { recordId }), [recordId, isReadOnly])
  const registeredTabs = useSlot(slots.tabs, 'tab', context)
  const contributedTabs = useMemo(() => inOrder(registeredTabs, tabOrder ?? []), [registeredTabs, tabOrder])
  const contributedLinks = useSlot(slots.menu, 'link', context)
  const accessStatus = useLinkAccessStatus()
  // The chosen tab is in the address, so a page that returns here, such as a player's Back, opens it again.
  const [params, setParams] = useSearchParams()
  const chosen = params.get(tabParam) ?? ''
  // Each contributed tab's count, for the record shown.
  const [counted, setCounted] = useState<{ readonly recordId: string; readonly counts: ReadonlyMap<string, TabCount | null> }>({ recordId, counts: new Map() })
  const counts = counted.recordId === recordId ? counted.counts : null
  const onCount = useCallback(
    (tabId: string, count: number | null, hasMore: boolean) =>
      setCounted((current) => {
        const next = count === null ? null : { count, hasMore }
        if (current.recordId !== recordId) {
          return { recordId, counts: new Map([[tabId, next]]) }
        }
        const known = current.counts.get(tabId)
        const same = known === null || next === null ? known === next : known?.count === next.count && known.hasMore === next.hasMore
        return same ? current : { recordId, counts: new Map(current.counts).set(tabId, next) }
      }),
    [recordId],
  )

  const tabItems = useMemo(
    () => [
      ...tabs.map((entry) => ({ id: entry.id, label: entry.label })),
      ...contributedTabs.map((entry) => {
        const label = intl.formatMessage({ id: entry.labelId })
        const tabCount = counts?.get(entry.id) ?? null
        return {
          id: entry.id,
          label: tabCount === null ? label : intl.formatMessage({ id: tabCount.hasMore ? 'details.tabs.countMore' : 'details.tabs.count' }, { label, count: tabCount.count }),
        }
      }),
    ],
    [tabs, contributedTabs, counts, intl],
  )
  // The first tab when none is chosen or the chosen one is gone, as when the page's tabs change.
  const first = tabItems[0]?.id ?? ''
  const isChosenShown = tabItems.some((item) => item.id === chosen)
  const tab = isChosenShown ? chosen : first
  // A chosen tab the viewer's access may yet show waits for it, rather than flashing the first.
  const isTabPending = chosen !== '' && !isChosenShown && accessStatus === 'loading'
  // Replaced, not added, so Back leaves the page rather than stepping through its tabs; a move
  // within the page, so a draft kept for this visit outlives the switch unasked.
  const location = useLocation()
  const setTab = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params)
      if (id === first) {
        next.delete(tabParam)
      } else {
        next.set(tabParam, id)
      }
      setParams(next, { replace: true, state: withinPage(location) })
    },
    [first, location, params, setParams],
  )

  const groups = useMemo<readonly DetailsMenuGroup[]>(() => {
    const own = props.menu ?? []
    const offered = isReadOnly ? contributedLinks.filter((link) => link.isCreate !== true) : contributedLinks
    if (offered.length === 0) {
      return own
    }
    const items = offered.map(
      (link): DetailsMenuItem => ({
        id: link.id,
        label: intl.formatMessage({ id: link.labelId }),
        icon: link.icon ?? Puzzle,
        path: typeof link.path === 'string' ? link.path : link.path(context),
      }),
    )
    return [...own, { id: 'contributed', title: intl.formatMessage({ id: 'details.menu.more' }), items }]
  }, [props.menu, contributedLinks, context, intl, isReadOnly])

  const own = tabs.find((entry) => entry.id === tab)
  const contributed = contributedTabs.find((entry) => entry.id === tab)

  // The tabs sit beside the side menu, or narrowed by zoom, so their tables stack by their own room.
  return (
    <NarrowPanelContext value>
      <PageContainer>
        <DetailsHeader {...props.header} actionSlot={slots.actions} context={context} {...(refreshKey === undefined ? {} : { refreshKey })} />

        <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
          {groups.length > 0 && <DetailsSidebar groups={groups} />}
          <div className="min-w-0 flex-1">
            {props.summary}
            {isTabPending ? (
              // <output> carries role="status" semantics without ARIA.
              <output className="flex flex-col gap-6">
                <span className="sr-only">{intl.formatMessage({ id: 'details.tabs.loading' })}</span>
                <Skeleton className="h-[43px] w-96 max-w-full" />
                <Skeleton className="h-40" />
              </output>
            ) : (
              <>
                {tabItems.length > 1 && (
                  <Tabs
                    tabs={tabItems}
                    value={tab}
                    onChange={setTab}
                    label={intl.formatMessage({ id: 'details.tabs.label' })}
                    idPrefix={idPrefix}
                    className="mb-6"
                  />
                )}
                <div key={`tab:${String(refreshKey ?? 0)}`} {...(tabItems.length > 1 ? tabPanelProps(idPrefix, tab) : {})}>
                  {own !== undefined && own.content}
                  {contributed !== undefined && <SlotContent contribution={contributed} context={context} />}
                </div>
              </>
            )}

            <SlotPanels key={`panels:${String(refreshKey ?? 0)}`} slot={slots.panels} context={context} className="mt-8 flex flex-col gap-6" />

            {props.audit !== undefined && <CreatedModified audit={props.audit} className="mt-8" />}
            {/* Keyed by the record, so a count never carries over to the next one, and counted again after a change. */}
            {contributedTabs.map((entry) => (
              <SlotCount key={`${recordId}\u0000${String(refreshKey ?? 0)}\u0000${entry.id}`} contribution={entry} context={context} onCount={onCount} />
            ))}
          </div>
        </div>
      </PageContainer>
    </NarrowPanelContext>
  )
}

/** A tab's count, and whether there are more than it, as a counter that reads one page reports. */
interface TabCount {
  readonly count: number
  readonly hasMore: boolean
}

/** The tabs `order` names, in its order, then the rest in registry order. */
function inOrder(tabs: readonly RegisteredTab[], order: readonly string[]): readonly RegisteredTab[] {
  const place = (tab: RegisteredTab) => {
    const index = order.indexOf(tab.id)
    return index === -1 ? order.length : index
  }
  return tabs.toSorted((first, second) => place(first) - place(second))
}
